import {
  selectIsLocalVideoEnabled,
  selectLocalPeer,
  selectVideoTrackByID,
  useHMSStore,
  useVideo,
} from "@100mslive/react-sdk";
import { Loader2, RotateCw, VideoOff } from "lucide-react";
import { PeerAvatar } from "@/components/common/PeerAvatar";
import { SupportLink } from "@/components/common/SupportLink";
import { Button } from "@/components/ui/button";
import { useMirrorPreference } from "@/hooks/usePreferences";
import { cn } from "@/lib/utils";

/** Your own camera before joining, or your initials when it is off. */
export const PreviewTile = ({ name, failed }) => {
  const localPeer = useHMSStore(selectLocalPeer);
  const isVideoOn = useHMSStore(selectIsLocalVideoEnabled);
  const track = useHMSStore(selectVideoTrackByID(localPeer?.videoTrack));
  const { videoRef } = useVideo({ trackId: localPeer?.videoTrack });
  const [mirror] = useMirrorPreference();

  return (
    <div className="relative aspect-video w-full overflow-hidden border-y bg-stage text-stage-foreground">
      {localPeer ? (
        <>
          <video
            ref={videoRef}
            autoPlay
            muted
            playsInline
            data-testid="preview_tile"
            className={cn(
              "size-full object-cover",
              mirror && track?.facingMode !== "environment" && "-scale-x-100",
              !isVideoOn && "invisible"
            )}
          />
          {!isVideoOn ? (
            <div
              className="absolute inset-0 flex flex-col items-center justify-center gap-3"
              data-testid="preview_avatar_tile"
            >
              <PeerAvatar name={name} />
              <span className="max-w-[75%] truncate text-sm text-stage-foreground/80">
                {name || "Your camera is off"}
              </span>
            </div>
          ) : null}
        </>
      ) : failed ? (
        // Some browsers (Safari) report the failure only here, with no error
        // dialog on top, so this needs its own way out (project rule 6).
        <div
          className="absolute inset-0 flex flex-col items-center justify-center gap-3 px-6 text-center text-sm text-stage-foreground/80"
          data-testid="preview_failed"
        >
          <VideoOff className="size-6" />
          We couldn&apos;t start your camera and microphone preview.
          <Button
            type="button"
            size="sm"
            variant="secondary"
            onClick={() => window.location.reload()}
            data-testid="preview_retry_btn"
          >
            <RotateCw />
            Try again
          </Button>
          <span className="text-xs text-stage-foreground/70">
            Still not working? <SupportLink />.
          </span>
        </div>
      ) : (
        <div className="absolute inset-0 flex items-center justify-center">
          <Loader2 className="size-8 animate-spin text-stage-foreground/60" />
        </div>
      )}
    </div>
  );
};
